import React, { useEffect, useRef, useState } from 'react';
import { Icon, Checkbox, Typography } from 'antd';
import cn from 'classnames';
import { useTreeCheckDispatch } from '../../context';
import { treeCheckActionTypes as actionTypes } from '../../state';
import TreeCheckNodeList from '../TreeCheckNodeList/TreeCheckNodeList';
import styles from './TreeCheckNode.module.scss';

const { Text } = Typography;

const getAddedCheckedKeys = (items = []) =>
  items.reduce((acc, item) => {
    acc.add(item.key);
    return acc;
  }, new Set());

const TreeCheckNode = ({
  node,
  expanded,
  loaded,
  checked = false,
  halfChecked = false,
  loadData,
  onExpand,
  onCheck,
  showExpandIcon,
  checkable,
}) => {
  const isMounted = useRef(true);

  const [expandLoading, setExpandLoading] = useState(false);

  const dispatch = useTreeCheckDispatch();

  const isLeaf = () => {
    if (node.isLeaf === false) {
      return false;
    }

    const hasChildren = Boolean(node.children?.length);

    return (
      node.isLeaf ||
      (!loadData && !hasChildren) ||
      (loadData && loaded && !hasChildren)
    );
  };

  const handleToggleExpand = (e) => {
    if (expandLoading) return;

    const targetExpanded = !expanded;

    if (typeof onExpand === 'function') {
      onExpand({
        node,
        expanded: targetExpanded,
        nativeEvent: e.nativeEvent,
      });
    }

    if (typeof loadData === 'function' && !loaded) {
      const promise = loadData(node);

      if (promise) {
        setExpandLoading(true);

        promise
          .then(() => {
            dispatch({
              type: actionTypes.toggleExpandAction,
              payload: {
                key: node.key,
                addKeyToLoadedKeys: targetExpanded,
                addKeyToExpandedKeys: targetExpanded,
                addedCheckedKeys:
                  targetExpanded && checked
                    ? getAddedCheckedKeys(node.children)
                    : new Set(),
              },
            });
          })
          .finally(() => {
            setExpandLoading(false);
          });
      }
    } else {
      dispatch({
        type: actionTypes.toggleExpandAction,
        payload: {
          key: node.key,
          addKeyToLoadedKeys: false,
          addKeyToExpandedKeys: targetExpanded,
          addedCheckedKeys:
            targetExpanded && checked
              ? getAddedCheckedKeys(node.children)
              : new Set(),
        },
      });
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      loaded &&
      !node.children?.length &&
      typeof loadData === 'function' &&
      isMounted.current
    ) {
      const promise = loadData(node);

      if (promise) {
        setExpandLoading(true);

        promise
          .then(() => {
            dispatch({
              type: actionTypes.toggleExpandAction,
              payload: {
                key: node.key,
                addKeyToLoadedKeys: true,
                addKeyToExpandedKeys: checked ? false : expanded,
                addedCheckedKeys:
                  expanded && checked
                    ? getAddedCheckedKeys(node.children)
                    : new Set(),
              },
            });
          })
          .finally(() => {
            setExpandLoading(false);
          });
      }
    }
  }, [JSON.stringify(node)]);

  return (
    <li className={styles.root}>
      <div className={styles.node}>
        {showExpandIcon && (
          <div className={styles.iconWrap}>
            {!isLeaf() ? (
              <Icon
                className={styles.icon}
                onClick={handleToggleExpand}
                type={
                  expandLoading
                    ? 'loading'
                    : expanded
                    ? 'caret-down'
                    : 'caret-right'
                }
              />
            ) : null}
          </div>
        )}

        <Checkbox
          onChange={checkable ? (e) => onCheck(e, node, !checked) : undefined}
          disabled={!checkable}
          checked={checked}
          indeterminate={halfChecked}
          className={styles.checkbox}
        />
        <Text
          className={cn(styles.title, {
            [styles.active]: !isLeaf(),
          })}
          onClick={isLeaf() ? undefined : handleToggleExpand}
        >
          {node.title}
        </Text>
      </div>
      {expanded && node.children?.length ? (
        <TreeCheckNodeList
          treeData={node.children}
          loadData={loadData}
          onExpand={onExpand}
          onCheck={onCheck}
          className={cn(styles.list, { [styles.newList]: showExpandIcon })}
          checkable={checkable}
          itemsToRender={node.children}
        />
      ) : null}
    </li>
  );
};

export default TreeCheckNode;
